import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { RouterModule } from '@angular/router';
import { FeedbackService } from '../../../services/feedback.service';
import { SupabaseService } from '../../../services/supabase.service';
import { TranslationService } from '../../../services/translation.service';
import { FeedbackInboxComponent } from '../../../components/feedback-inbox/feedback-inbox.component';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { includesEveryNameToken } from '../../../models/name-search';

interface LinkedHuman { id: string; name: string; }
interface RegisteredUser {
  id: string; full_name: string | null; username: string | null; created_at: string | null; is_admin: boolean;
  linkedHumans: LinkedHuman[];
}

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, TranslatePipe, RouterModule, FeedbackInboxComponent, PageHeaderComponent],
  templateUrl: './admin-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboardComponent {
  private supabase = inject(SupabaseService);
  showRegisteredUsers = signal(false);
  usersLoading = signal(false);
  usersError = signal(false);
  usersSearch = signal('');
  registeredUsers = signal<RegisteredUser[]>([]);
  unlinkingHumanId = signal<string | null>(null);
  unlinkError = signal(false);
  unlinkSuccess = signal(false);
  filteredUsers = computed(() => {
    const query = this.usersSearch().trim();
    return this.registeredUsers().filter(user => !query || includesEveryNameToken(
      `${user.full_name || ''} ${user.username || ''} ${user.linkedHumans.map(human => human.name).join(' ')}`, query));
  });

  async viewRegisteredUsers() {
    this.showRegisteredUsers.set(true);
    this.usersLoading.set(true);
    this.usersError.set(false);
    try {
      const rows: RegisteredUser[] = [];
      for (let offset = 0; ; offset += 1000) {
        const { data, error } = await this.supabase.client.from('profiles')
          .select('id, full_name, username, created_at, is_admin, linkedHumans:humans!humans_profile_id_fkey(id,name)')
          .order('created_at', { ascending: false }).order('id').range(offset, offset + 999);
        if (error) throw error;
        rows.push(...(data || []).map(user => ({ ...user, linkedHumans: user.linkedHumans || [] })));
        if (!data || data.length < 1000) break;
      }
      this.registeredUsers.set(rows);
    } catch {
      this.usersError.set(true);
    } finally {
      this.usersLoading.set(false);
    }
  }

  async unlinkHuman(user: RegisteredUser, human: LinkedHuman) {
    if (this.unlinkingHumanId() || !confirm(this.translations.translate('admin.dashboard.unlinkConfirm', human.name, user.full_name || user.username || '—'))) return;
    this.unlinkingHumanId.set(human.id);
    this.unlinkError.set(false);
    this.unlinkSuccess.set(false);
    try {
      const { error } = await this.supabase.client.rpc('unlink_human_profile_as_admin', {
        p_human_id: human.id,
        p_profile_id: user.id,
      });
      if (error) throw error;
      this.registeredUsers.update(users => users.map(item => item.id === user.id
        ? { ...item, linkedHumans: item.linkedHumans.filter(linked => linked.id !== human.id) }
        : item));
      this.unlinkSuccess.set(true);
    } catch {
      this.unlinkError.set(true);
    } finally {
      this.unlinkingHumanId.set(null);
    }
  }
  readonly feedback = inject(FeedbackService);
  private translations = inject(TranslationService);
  pending = signal<number | null>(null);
  unpublished = signal<number | null>(null);
  countsError = signal(false);

  constructor() { void this.loadCounts(); }
  async loadCounts() {
    this.countsError.set(false);
    try {
      const [imports, events] = await Promise.all([
        this.supabase.client.from('staging_events').select('id', { count: 'exact', head: true }).eq('import_status', 'pending'),
        this.supabase.client.from('events').select('id', { count: 'exact', head: true }).eq('is_published', false)
      ]);
      if (imports.error || events.error) throw imports.error || events.error;
      this.pending.set(imports.count ?? 0);
      this.unpublished.set(events.count ?? 0);
    } catch { this.countsError.set(true); }
  }

  adminLinks = [
    { view: '/admin/resultats', titleKey: 'adminTools.events', descriptionKey: 'adminTools.eventsHint' },
    { view: '/admin/staging', titleKey: 'adminTools.imports', descriptionKey: 'adminTools.importsHint' },
    { view: '/admin/import-ranking', titleKey: 'adminTools.file', descriptionKey: 'pasteImport.hint' },
    { view: '/admin/dashboard', fragment: 'messages', titleKey: 'adminTools.messages', descriptionKey: 'adminTools.messagesHint' }
  ].map(link => ({ ...link, fragment: 'fragment' in link ? link.fragment : undefined,
    icon: 'M8 7h8M8 12h8M8 17h5M5 3h14v18H5z' }));

  secondaryLinks = [
    { view: '/admin/ffslc-races', title: 'header.ffslcRaces' },
    { view: '/admin/clubs', title: 'adminTools.clubs' },
    { view: '/admin/dogs', title: 'adminTools.breeds' },
    { view: '/admin/categories', title: 'adminTools.categories' }
  ];
}
